import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function BeginnerTipsPage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kakobuy Spreadsheet for Beginners: 10 Essential Tips to Start 2026",
    "description": "New to Kakobuy shopping? These ten essential beginner tips combined with our curated spreadsheet will help you navigate the platform with confidence and avoid common pitfalls.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/beginner-tips"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20clean%20desk%20setup%20with%20a%20notebook%20open%20showing%20a%20numbered%20list%20beside%20a%20laptop%20displaying%20an%20online%20shopping%20interface%20a%20pencil%20and%20a%20pair%20of%20stylish%20accessories%20arranged%20neatly%20soft%20natural%20light%20from%20an%20overhead%20source%20warm%20and%20inviting%20workspace%20photography%20with%20organized%20aesthetic&width=1400&height=600&seq=article-beginner-v2-07&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Beginner Tips</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              <span className="text-primary-500">Kakobuy Spreadsheet</span> for Beginners: 10 Essential Tips to Start
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>6 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20clean%20desk%20setup%20with%20a%20notebook%20open%20showing%20a%20numbered%20list%20beside%20a%20laptop%20displaying%20an%20online%20shopping%20interface%20a%20pencil%20and%20a%20pair%20of%20stylish%20accessories%20arranged%20neatly%20soft%20natural%20light%20from%20an%20overhead%20source%20warm%20and%20inviting%20workspace%20photography%20with%20organized%20aesthetic&width=1400&height=600&seq=article-beginner-v2-07&orientation=landscape"
                alt="Kakobuy Spreadsheet Beginner Tips" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Getting Started with Kakobuy Spreadsheet</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Starting your journey with <strong>Kakobuy Spreadsheet</strong> shopping can feel overwhelming, but it does not have to be. The spreadsheet was specifically designed to make the platform accessible to newcomers while providing enough depth for experienced shoppers. Here are the essential tips every beginner needs to know.
              </p>
              <div className="space-y-3">
                {[
                  { tip: "Start with Popular Categories", desc: "Begin with high-volume categories like Shoes and T-Shirts where the spreadsheet has the most verified listings and community feedback." },
                  { tip: "Always Check Seller Ratings", desc: "The trust score column in the Kakobuy Spreadsheet is your best friend. Stick with sellers rated 90% or above for your first few purchases." },
                  { tip: "Read QC Photos Carefully", desc: "Never skip the QC photo links. They show you the actual product, not just the polished listing images. Look for clear, well-lit photos from multiple angles." },
                  { tip: "Use the Size Charts", desc: "Asian sizing differs from Western standards. The spreadsheet includes conversion charts for every clothing category. Measure yourself and compare before ordering." },
                  { tip: "Start with Small Orders", desc: "Limit your first order to 2-3 items. This lets you experience the process end-to-end — ordering, shipping, and delivery — without a large financial commitment." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-background-50">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.tip}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}